import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {isColumnTypeCompatible} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import ColumnDropdownSelector from 'data-frames/column-selector/ColumnDropdownSelector';
import {DataFrameColumnDef, DataFrameColumns, DataFrameColumnType} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {readableTypeDef} from 'data-types/benchling-type-def/readableTypeDef';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {capitalize} from 'util/NameUtil';

export interface DataFrameColumnMappingError {
  systemName: string;
  title: string;
  description: string;
  level: 'ERROR' | 'WARNING';
}

export interface DataFrameColumnMappingTableProps {
  templateDataFrameColumns: Array<DataFrameColumnDef>;
  newDataFrameColumns: DataFrameColumns | null;
  setSelectedColumns: (newColumnSystemNameOrNull: Array<DataFrameColumnDef | null>) => void;
  selectedColumns: Array<DataFrameColumnDef | null>;
  errors?: Array<DataFrameColumnMappingError>;
  noColumnSelectedText?: string;
  ignoreColumnValidationErrors?: boolean;
  isInputTableColumnUnique?: boolean;
}

interface ColumnDropdownSelectorWithBannerProps {
  newDataFrameColumns: DataFrameColumns;
  setSelectedColumns: (newColumnSystemNameOrNull: Array<DataFrameColumnDef | null>) => void;
  selectedColumns: Array<DataFrameColumnDef | null>;
  noColumnSelectedText?: string;
  ignoreColumnValidationErrors?: boolean;
  errors?: Array<DataFrameColumnMappingError>;
  templateColumn: DataFrameColumnDef;
  newDataFrameColumnsBySystemName: Record<string, DataFrameColumnDef>;
  columnIndex: number;
  /**
   * Enforces that any column in the input table can only be mapped to one column in the template table.
   */
  isInputTableColumnUnique?: boolean;
}
const NULLABLE_DESCRIPTOR = 'that allows empty cells';

function makeColumnTypeDescription(columnType: DataFrameColumnType): string {
  // e.g. 'boolean list column that allows empty cells'
  let description = readableTypeDef(columnType.leafType);
  if (columnType.isList) {
    description += ' list';
  }
  description += ' column';
  if (columnType.isNullable) {
    description += ` ${NULLABLE_DESCRIPTOR}`;
  }

  return description;
}

export function makeTypeMismatchWarning(
  templateColumnType: DataFrameColumnType,
  selectedColumnType: DataFrameColumnType
): string {
  const templateColumnDescription = makeColumnTypeDescription(templateColumnType);
  const selectedColumnDescription = makeColumnTypeDescription(selectedColumnType);

  return `${capitalize(selectedColumnDescription)} selected for ${templateColumnDescription}`;
}

const ERROR_LEVEL_SCORE: Record<DataFrameColumnMappingError['level'], number> = {
  // The lower the more urgent
  WARNING: 1,
  ERROR: 0,
};

function ColumnDropdownSelectorWithBanner({
  newDataFrameColumns,
  setSelectedColumns,
  selectedColumns,
  noColumnSelectedText,
  ignoreColumnValidationErrors,
  errors,
  templateColumn,
  newDataFrameColumnsBySystemName,
  columnIndex,
  isInputTableColumnUnique,
}: ColumnDropdownSelectorWithBannerProps): JSX.Element {
  const selectedColumn = selectedColumns[columnIndex];
  const errorToShow =
    (errors ?? []).sort((e1, e2) => ERROR_LEVEL_SCORE[e1.level] - ERROR_LEVEL_SCORE[e2.level])[0] ?? null;

  const otherSelectedColumns = [
    ...selectedColumns.slice(0, columnIndex),
    ...selectedColumns.slice(columnIndex + 1),
  ];
  const columnOptions = isInputTableColumnUnique
    ? newDataFrameColumns.filter((column) => !otherSelectedColumns.includes(column))
    : newDataFrameColumns;
  const dropdown = (
    <ColumnDropdownSelector
      buttonClassName={css(
        styles.columnSelector,
        errorToShow && errorToShow.level === 'WARNING' && styles.columnSelectorWarning,
        errorToShow && errorToShow.level === 'ERROR' && styles.columnSelectorError
      )}
      noColumnSelectedText={noColumnSelectedText}
      dataFrameSpec={{columns: columnOptions ?? []}}
      selectedColumnSystemName={selectedColumn ? selectedColumn.systemName : null}
      onChange={(newColumnSystemNameOrNull) => {
        const newSelectedColumns = [...selectedColumns];
        if (newColumnSystemNameOrNull == null) {
          newSelectedColumns[columnIndex] = null;
        } else {
          newSelectedColumns[columnIndex] = newDataFrameColumnsBySystemName[newColumnSystemNameOrNull];
        }
        setSelectedColumns(newSelectedColumns);
      }}
      hideTypeIcon={ignoreColumnValidationErrors}
      allowSelectNull={true}
      dataTestAttrs={{
        component: 'DataFrameColumnMappingTable',
        element: 'columnDropdownSelector',
        index: String(columnIndex),
      }}
    />
  );
  if (!errorToShow) {
    return dropdown;
  }

  return (
    <div>
      {dropdown}
      <div className={css(styles.bannerWrapper)}>
        <InlineBanner
          bannerType={
            (errorToShow.level === 'ERROR' && InlineBannerType.ERROR) ||
            (errorToShow.level === 'WARNING' && InlineBannerType.WARNING) ||
            InlineBannerType.INFO
          }
          text={errorToShow.title}
          supportingInfo={errorToShow.description}
          dataTestAttrs={{
            component: 'DataFrameColumnMappingTable',
            element: 'errorBanner',
          }}
        />
      </div>
    </div>
  );
}

export function DataFrameColumnMappingTable({
  templateDataFrameColumns,
  newDataFrameColumns,
  setSelectedColumns,
  selectedColumns,
  noColumnSelectedText,
  errors,
  ignoreColumnValidationErrors,
  isInputTableColumnUnique,
}: DataFrameColumnMappingTableProps): JSX.Element {
  if (errors == null) {
    // If errors are not provided and not ignoring validation errors,
    // this component run some default validations and generate warnings.
    errors = [];
    if (!ignoreColumnValidationErrors) {
      for (let i = 0; i < templateDataFrameColumns.length; i++) {
        const templateColumnDef = templateDataFrameColumns[i];
        const inputColumnDef = selectedColumns[i];

        if (inputColumnDef == null) {
          continue;
        }
        if (isColumnTypeCompatible(inputColumnDef, templateColumnDef)) {
          continue;
        }

        errors.push({
          systemName: templateColumnDef.systemName,
          title: 'Analysis may fail due to type mismatch',
          description: makeTypeMismatchWarning(templateColumnDef.type, inputColumnDef.type),
          level: 'WARNING',
        });
      }
    }
  }

  const newDataFrameColumnsBySystemName: Record<string, DataFrameColumnDef> = objectFromEntries(
    (newDataFrameColumns ?? []).map((c) => [c.systemName, c])
  );

  return (
    <>
      {templateDataFrameColumns.map((templateColumn, columnIndex) => (
        <Row key={columnIndex} className={css(styles.row)}>
          <Col md={6}>
            <BenchlingIcon className={css(styles.typeIcon)} icon={columnToTypeIcon(templateColumn)} />
            <span className={classNames('u-mini-spacing-left', css(styles.span))}>
              {templateColumn.displayName}
            </span>
          </Col>
          <Col md={6}>
            {newDataFrameColumns ? (
              <ColumnDropdownSelectorWithBanner
                errors={(errors ?? []).filter((e) => e.systemName === templateColumn.systemName)}
                newDataFrameColumnsBySystemName={newDataFrameColumnsBySystemName}
                noColumnSelectedText={noColumnSelectedText}
                setSelectedColumns={setSelectedColumns}
                columnIndex={columnIndex}
                newDataFrameColumns={newDataFrameColumns}
                selectedColumns={selectedColumns}
                templateColumn={templateColumn}
                isInputTableColumnUnique={isInputTableColumnUnique}
              />
            ) : null}
          </Col>
        </Row>
      ))}
    </>
  );
}

const styles = StyleSheet.create({
  span: {wordWrap: 'break-word'},
  leftContentPane: {
    flex: 1,
    ...FONT_PROXIMA_13,
    padding: 16,
  },
  previewTableWrapper: {
    maxHeight: 350,
  },
  typeIcon: {
    color: Colors.GRAY_DARK,
  },
  row: {
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderTopWidth: 1,
    paddingTop: 5,
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
  },
  columnSelector: {
    minHeight: 34,
  },
  columnSelectorWarning: {
    borderColor: Colors.ORANGE,
  },
  columnSelectorError: {
    borderColor: Colors.RED,
  },
  bannerWrapper: {
    marginTop: 5,
  },
});
